import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import PageLayout from '@/components/layout/PageLayout'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { IntuiBadge } from '@/components/intueri/IntuiBadge'
// Card grid uses raw divs for full layout control
import {
  IntuiTabs,
  IntuiTabsList,
  IntuiTabsTrigger,
} from '@/components/intueri/IntuiTabs'
import {
  IntuiDropdownMenu,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuTrigger,
} from '@/components/intueri/IntuiDropdownMenu'
import {
  IntuiAlertDialog,
  IntuiAlertDialogAction,
  IntuiAlertDialogCancel,
  IntuiAlertDialogContent,
  IntuiAlertDialogDescription,
  IntuiAlertDialogFooter,
  IntuiAlertDialogHeader,
  IntuiAlertDialogTitle,
} from '@/components/intueri/IntuiAlertDialog'
import {
  Search, Plus, MessageSquare, Phone, MoreVertical, Loader2,
  List, LayoutGrid, Eye, Pencil, Trash, ListFilter, Check,
} from 'lucide-react'
import { apiService } from '@/service/api'
import type { DigitalCoworkerRecord } from '@/service/api'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { formatRelativeTime } from '@/utils/timeUtils'
import { DigitalCoworkerChatSheet } from './DigitalCoworkerChatSheet'
import { DigitalCoworkerDetailsDialog } from './DigitalCoworkerDetailsDialog'
import DigitalCoworkerCallModal from '@/components/DigitalCoworkerCallModal'
import { coworkerAvatarUrl } from '@/utils/coworkerAvatar'

type StatusFilter = 'all' | 'pending' | 'training' | 'active'

/** UI-21: the statuses the Filters menu offers, in the order they read. */
const STATUS_FILTERS: ReadonlyArray<{ value: StatusFilter; key: string; fallback: string }> = [
  { value: 'all', key: 'digital_coworker:status_filter.all', fallback: 'All Status' },
  { value: 'active', key: 'digital_coworker:status_filter.active', fallback: 'Active' },
  { value: 'training', key: 'digital_coworker:status_filter.training', fallback: 'Training' },
  { value: 'pending', key: 'digital_coworker:status_filter.pending', fallback: 'Pending' },
]
type ViewMode = 'list' | 'grid'

const getInitials = (name: string) =>
  name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()

const statusVariant = (s: string) => {
  if (s === 'active') return 'success' as const
  if (s === 'training') return 'warning' as const
  return 'secondary' as const
}

export default function DigitalCoworkerV2() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [coworkers, setCoworkers] = useState<DigitalCoworkerRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [chatCoworker, setChatCoworker] = useState<DigitalCoworkerRecord | null>(null)
  const [callCoworker, setCallCoworker] = useState<DigitalCoworkerRecord | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [detailsCoworker, setDetailsCoworker] = useState<DigitalCoworkerRecord | null>(null)
  const requestIdRef = useRef(0)

  const fetchCoworkers = useCallback(async (showLoader = true, signal?: AbortSignal) => {
    const reqId = showLoader ? ++requestIdRef.current : requestIdRef.current
    if (showLoader) setIsLoading(true)
    try {
      const result = await apiService.listDigitalCoworkers({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined,
      }, { signal })
      if (signal?.aborted || reqId !== requestIdRef.current) return
      setCoworkers(result.digital_coworkers)
    } catch (error) {
      if ((error as Error).name === 'AbortError') return
      if (reqId !== requestIdRef.current) return
      console.error('Failed to fetch digital coworkers:', error)
      if (showLoader) toast.error(t('digital_coworker:toast.load_error'))
    } finally {
      if (showLoader && reqId === requestIdRef.current) setIsLoading(false)
    }
  }, [statusFilter, searchTerm, t])

  const fetchRef = useRef(fetchCoworkers)
  useEffect(() => { fetchRef.current = fetchCoworkers }, [fetchCoworkers])

  useEffect(() => {
    const controller = new AbortController()
    const timer = setTimeout(() => { void fetchCoworkers(true, controller.signal) }, 300)
    return () => { clearTimeout(timer); controller.abort() }
  }, [fetchCoworkers])

  useEffect(() => {
    const controller = new AbortController()
    const interval = setInterval(() => { void fetchRef.current(false, controller.signal) }, 5000)
    return () => { clearInterval(interval); controller.abort() }
  }, [])

  const handleDelete = async () => {
    if (!deleteTarget) return
    const { id, name } = deleteTarget
    setDeleteTarget(null)
    try {
      await apiService.deleteDigitalCoworker(id)
      toast.success(t('digital_coworker:toast.deleted', { name }))
      void fetchCoworkers()
    } catch {
      toast.error(t('digital_coworker:toast.delete_error'))
    }
  }

  const avatarUrl = (c: DigitalCoworkerRecord) => coworkerAvatarUrl(c)

  const actionButtons = (c: DigitalCoworkerRecord) => (
    <div className="flex items-center gap-1.5">
      <IntuiButton variant="outline" size="xs" leftIcon={<MessageSquare />} onClick={() => navigate(`/digital-coworker/${c.id}/chat`)}>
        {t('digital_coworker:actions.chat', 'Chat')}
      </IntuiButton>
      <IntuiButton variant="outline" size="xs" leftIcon={<Phone />} onClick={() => setCallCoworker(c)}>
        {t('digital_coworker:actions.call', 'Call')}
      </IntuiButton>
      <IntuiDropdownMenu>
        <IntuiDropdownMenuTrigger asChild>
          <IntuiButton variant="ghost" size="icon-xs"><MoreVertical /></IntuiButton>
        </IntuiDropdownMenuTrigger>
        <IntuiDropdownMenuContent align="end">
          <IntuiDropdownMenuItem icon={<Eye />} onClick={() => setDetailsCoworker(c)}>
            {t('digital_coworker:dropdown.view_details')}
          </IntuiDropdownMenuItem>
          <IntuiDropdownMenuItem
            icon={<Pencil />}
            onClick={() => navigate('/digital-coworker/new', {
              state: { coworkerId: c.id, avatarUrl: avatarUrl(c), formData: { type: c.type, name: c.name, jobTitle: c.job_title, department: c.department, expertise: c.expertise || '', voice: c.voice ?? '' } },
            })}
            disabled={c.type === 'colleague'}
          >
            {t('digital_coworker:dropdown.edit')}
          </IntuiDropdownMenuItem>
          <IntuiDropdownMenuItem
            variant="destructive"
            icon={<Trash />}
            onClick={() => setDeleteTarget({ id: c.id, name: c.name })}
          >
            {t('digital_coworker:dropdown.delete')}
          </IntuiDropdownMenuItem>
        </IntuiDropdownMenuContent>
      </IntuiDropdownMenu>
    </div>
  )

  return (
    <PageLayout bgColor="bg-white dark:bg-dark-card" fullScreenContent>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="relative w-[280px]">
            <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <IntuiInput
              placeholder={t('digital_coworker:search_placeholder', 'Search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-9 ps-9 rounded-lg"
            />
          </div>
          {/* UI-21: the same filter trigger the Data Catalog and Tag Mapping
              use — outline, size lg, the ListFilter glyph, a visible label.
              This was an "All Status" select, which is the control Pegah
              screenshotted next to the catalog's filter button.

              A select shows its current value in the trigger and a menu does
              not, so the chip beside it carries that. Clearing is picking
              "All Status" from the menu, exactly as the select worked. */}
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <IntuiButton
                variant="outline"
                size="lg"
                active={statusFilter !== 'all'}
              >
                <ListFilter className="size-4" />
                {t('digital_coworker:filter.button', 'Filters')}
              </IntuiButton>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent align="start" className="w-[200px]">
              {STATUS_FILTERS.map((option) => (
                <IntuiDropdownMenuItem
                  key={option.value}
                  onSelect={() => setStatusFilter(option.value)}
                >
                  <span className="flex-1">{t(option.key, option.fallback)}</span>
                  {statusFilter === option.value && <Check className="size-4" />}
                </IntuiDropdownMenuItem>
              ))}
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
          {statusFilter !== 'all' && (
            <span className="inline-flex items-center rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground">
              {t(
                STATUS_FILTERS.find((o) => o.value === statusFilter)?.key ?? '',
                STATUS_FILTERS.find((o) => o.value === statusFilter)?.fallback ?? '',
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <IntuiTabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <IntuiTabsList className="h-9">
              <IntuiTabsTrigger value="grid">
                <LayoutGrid className="size-4" />
              </IntuiTabsTrigger>
              <IntuiTabsTrigger value="list">
                <List className="size-4" />
              </IntuiTabsTrigger>
            </IntuiTabsList>
          </IntuiTabs>
          {/* data-tour-id: help-agent "digital-coworker" tour anchor
              (always rendered, list or empty state). */}
          <IntuiButton
            size="lg"
            leftIcon={<Plus />}
            onClick={() => navigate('/digital-coworker/new')}
            data-tour-id="dc-create"
          >
            {t('digital_coworker:new_button', 'New Coworker')}
          </IntuiButton>
        </div>
      </div>

      {/* Content — min-w-0 so the grid can shrink inside flex layout (fits viewport width) */}
      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 pb-4 sm:px-6 sm:pb-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : coworkers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <p className="text-sm">{t('digital_coworker:empty_state.message', 'No coworkers yet')}</p>
            <IntuiButton className="mt-4" leftIcon={<Plus />} onClick={() => navigate('/digital-coworker/new')}>
              {t('digital_coworker:empty_state.create_button', 'Create')}
            </IntuiButton>
          </div>
        ) : viewMode === 'list' ? (
          /* Table view */
          <div className="w-full min-w-0 overflow-x-auto">
            <div className="flex items-center border-b border-[#e5e5e5] dark:border-dark-border">
              {/* UI-49: the name column FLEXES. It used to be pinned at 224px
                  while Actions was the only `flex-1`, so Actions swallowed
                  every spare pixel and the name ellipsised at ~164px however
                  wide the row was. 224px stays as the floor so narrow
                  viewports are unchanged and the header keeps aligning with
                  the body row below, which carries the same classes. */}
              <span className="min-w-[224px] flex-1 px-2 py-4 text-sm font-medium text-muted-foreground">{t('digital_coworker:table.name', 'File name')}</span>
              <span className="w-[176px] shrink-0 px-2 py-4 text-sm font-medium text-muted-foreground">{t('digital_coworker:table.role', 'Role')}</span>
              <span className="w-[136px] shrink-0 overflow-hidden px-2 py-4 text-sm font-medium text-muted-foreground">{t('digital_coworker:table.status', 'Status')}</span>
              <span className="w-[88px] shrink-0 px-2 py-4 text-sm font-medium text-muted-foreground">{t('digital_coworker:table.interviews', 'Interviews')}</span>
              <span className="w-[88px] shrink-0 px-2 py-4 text-sm font-medium text-muted-foreground">{t('digital_coworker:table.documents', 'Documents')}</span>
              <span className="w-[112px] shrink-0 px-2 py-4 text-sm font-medium text-muted-foreground">{t('digital_coworker:table.integration', 'Integration')}</span>
              <span className="w-[128px] shrink-0 truncate px-2 py-4 text-sm font-medium text-muted-foreground">{t('digital_coworker:table.last_opened', 'Last Opened')}</span>
              <span className="w-[176px] shrink-0 px-2 py-4 text-sm font-medium text-muted-foreground">{t('digital_coworker:table.actions', 'Actions')}</span>
            </div>
            {coworkers.map((c) => (
              <div key={c.id} className="flex items-center h-14 border-b border-[#e5e5e5] dark:border-dark-border hover:bg-muted/50 transition-colors">
                <div className="min-w-[224px] flex-1 px-2 flex items-center gap-3">
                  <div className="size-8 shrink-0 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center font-semibold text-orange-600 text-sm overflow-hidden">
                    {avatarUrl(c) ? (
                      <img src={avatarUrl(c)!} alt={c.name} className="size-full object-cover" />
                    ) : getInitials(c.name)}
                  </div>
                  {/* min-w-0: a flex item defaults to `min-width: auto`, which
                      refuses to shrink below its content — so `truncate` fires
                      early instead of using the space the cell now has. */}
                  <span className="min-w-0 truncate text-sm font-medium">{c.name}</span>
                </div>
                <span className="w-[176px] shrink-0 px-2 text-sm text-muted-foreground truncate">{c.job_title}</span>
                <div className="w-[136px] shrink-0 overflow-hidden px-2">
                  <IntuiBadge variant={statusVariant(c.status)}>
                    {t(`digital_coworker:status_filter.${c.status}`, c.status)}
                  </IntuiBadge>
                </div>
                <span className="w-[88px] shrink-0 px-2 text-sm text-muted-foreground">{c.interviews_count}</span>
                <span className="w-[88px] shrink-0 px-2 text-sm text-muted-foreground">{c.indexed_documents_count}</span>
                <div className="w-[112px] shrink-0 px-2 flex gap-1">
                  {c.google_integration.is_connected && <IntuiBadge variant="outline">Google</IntuiBadge>}
                  {!c.google_integration.is_connected && <span className="text-sm text-muted-foreground">-</span>}
                </div>
                <span className="w-[128px] shrink-0 truncate px-2 text-sm text-muted-foreground">{formatRelativeTime(c.updated_at)}</span>
                <div className="w-[176px] shrink-0 px-2">{actionButtons(c)}</div>
              </div>
            ))}
          </div>
        ) : (
          /* Grid view — column count scales with screen; avatars fill the card header */
          <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
            {coworkers.map((c) => (
              <div
                key={c.id}
                className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-[#e5e5e5] bg-white shadow-xs dark:border-dark-border dark:bg-dark-card"
              >
                {/* Avatar area with gradient */}
                <div
                  className="relative h-36 w-full overflow-hidden"
                  style={{ background: 'linear-gradient(25deg, rgb(253, 164, 175) 0%, rgb(254, 215, 170) 100%)' }}
                >
                  {avatarUrl(c) ? (
                    <img
                      src={avatarUrl(c)!}
                      alt={c.name}
                      className="h-full w-full object-cover object-center"
                    />
                  ) : (
                    <div className="flex h-full items-end justify-center pb-8">
                      <span className="text-5xl font-bold text-white/50">{getInitials(c.name)}</span>
                    </div>
                  )}
                </div>

                {/* Card footer */}
                <div className="flex flex-col gap-4 p-4">
                  <div className="flex flex-col gap-0.5">
                    <p className="truncate text-base font-medium text-foreground">{c.name}</p>
                    <p className="truncate text-sm text-muted-foreground">{c.job_title}</p>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <IntuiButton variant="outline" size="sm" leftIcon={<MessageSquare />} onClick={() => navigate(`/digital-coworker/${c.id}/chat`)}>
                        {t('digital_coworker:actions.chat', 'Chat')}
                      </IntuiButton>
                      <IntuiButton variant="outline" size="sm" leftIcon={<Phone />} onClick={() => setCallCoworker(c)}>
                        {t('digital_coworker:actions.call', 'Call')}
                      </IntuiButton>
                    </div>
                    <IntuiDropdownMenu>
                      <IntuiDropdownMenuTrigger asChild>
                        <IntuiButton variant="ghost" size="icon-xs"><MoreVertical /></IntuiButton>
                      </IntuiDropdownMenuTrigger>
                      <IntuiDropdownMenuContent align="end">
                        <IntuiDropdownMenuItem icon={<Eye />} onClick={() => setDetailsCoworker(c)}>
                          {t('digital_coworker:dropdown.view_details')}
                        </IntuiDropdownMenuItem>
                        <IntuiDropdownMenuItem
                          icon={<Pencil />}
                          onClick={() => navigate('/digital-coworker/new', {
                            state: { coworkerId: c.id, avatarUrl: avatarUrl(c), formData: { type: c.type, name: c.name, jobTitle: c.job_title, department: c.department, expertise: c.expertise || '', voice: c.voice ?? '' } },
                          })}
                          disabled={c.type === 'colleague'}
                        >
                          {t('digital_coworker:dropdown.edit')}
                        </IntuiDropdownMenuItem>
                        <IntuiDropdownMenuItem
                          variant="destructive"
                          icon={<Trash />}
                          onClick={() => setDeleteTarget({ id: c.id, name: c.name })}
                        >
                          {t('digital_coworker:dropdown.delete')}
                        </IntuiDropdownMenuItem>
                      </IntuiDropdownMenuContent>
                    </IntuiDropdownMenu>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Details / Chat / Call / Delete */}
      <DigitalCoworkerDetailsDialog
        coworker={detailsCoworker}
        onOpenChange={(open) => { if (!open) setDetailsCoworker(null) }}
      />
      <DigitalCoworkerChatSheet coworker={chatCoworker} isOpen={chatCoworker !== null} onClose={() => setChatCoworker(null)} />
      <DigitalCoworkerCallModal coworker={callCoworker} isOpen={callCoworker !== null} onClose={() => setCallCoworker(null)} />

      <IntuiAlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <IntuiAlertDialogContent>
          <IntuiAlertDialogHeader>
            <IntuiAlertDialogTitle>{t('digital_coworker:delete_dialog.title')}</IntuiAlertDialogTitle>
            <IntuiAlertDialogDescription>
              {t('digital_coworker:delete_dialog.description', { name: deleteTarget?.name })}
            </IntuiAlertDialogDescription>
          </IntuiAlertDialogHeader>
          <IntuiAlertDialogFooter>
            <IntuiAlertDialogCancel>{t('digital_coworker:delete_dialog.cancel', 'Cancel')}</IntuiAlertDialogCancel>
            <IntuiAlertDialogAction variant="destructive" onClick={() => void handleDelete()}>
              {t('digital_coworker:delete_dialog.delete', 'Delete')}
            </IntuiAlertDialogAction>
          </IntuiAlertDialogFooter>
        </IntuiAlertDialogContent>
      </IntuiAlertDialog>
    </PageLayout>
  )
}
